<template>
    <div class="bs-container">
        <div class="pp-ticket-check-results-summary bs-row">
            <div class="pp-ticket-check-results-summary__section c-12 c-sm-6 c-md-5 bs-offset-md-1 c-lg-4 c-xl-5">
                <DsSmileStatic
                    :face-type="faceAttributes.type"
                    :face-position="faceAttributes.position"
                    :with-shadow="true"
                    shadow-color="black"
                    class="pp-ticket-check-results-summary__kulicnik"
                    :width="120"
                    :height="120"
                />
                <p v-html="formattedText" class="pp-ticket-check-results-summary__text"></p>
                <DsButton
                    @click="onButtonClick(ticketCheckResult.resultsTexts.buttonText)"
                    :href="ticketCheckResult.resultsTexts.buttonLink"
                    >{{ ticketCheckResult.resultsTexts.buttonText }}</DsButton
                >
                <DsTooltip
                    v-if="ticketCheckResult.resultsTexts.tooltipText"
                    :has-trigger="false"
                    placement="bottom"
                    class="pp-ticket-check-results-summary__tooltip"
                    :z-index="1"
                >
                    <p class="pp-ticket-check-results-summary__tooltip-text">
                        <span v-html="ticketCheckResult.resultsTexts.tooltipText"></span>
                    </p>
                </DsTooltip>
            </div>
            <div
                class="pp-ticket-check-results-summary__banners c-12 c-sm-6 c-md-5 bs-offset-md-1 c-lg-6 bs-offset-lg-0 c-xl-5"
            >
                <DsPromoBannerNew
                    v-for="(banner, idx) in ticketCheckResult.banners"
                    :key="banner.codeName"
                    :banner="banner"
                    @click="onBannerClick(banner, idx)"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted } from "vue";
import DsButton from "@sazka/design-system/src/button/ds-button.vue";
import DsPromoBannerNew from "@sazka/design-system/src/promo-banner/ds-promo-banner-new.vue";
import DsSmileStatic from "@sazka/design-system/src/smiles/ds-smile-static.vue";
import DsTooltip from "@sazka/design-system/src/tooltip/ds-tooltip.vue";
import { DsPromoBannerNewDto } from "@sazka/design-system/src/promo-banner/ds-promo-banner-new";
import {
    DsSmileStaticFacePosition,
    DsSmileStaticFaceType,
} from "@sazka/design-system/src/smiles/ds-smile-static-types";
import { StringUtils } from "@sazka/utils/string-utils";
import { AnalyticsPromo } from "@/shared/utils/analytics/analytics-promo";
import { ProductPageAnalytics } from "../../analytics/analytics-product-page";
import { usePpTicketCheck } from "../../composables/use-ticket-check";

const { ticketCheckResult } = usePpTicketCheck();

const formattedText = computed(() => {
    return ticketCheckResult.value.resultsTexts.text
        .replace(
            "{{AMOUNT}}",
            `<span class="pp-ticket-check-results-summary__text--amount">${StringUtils.formatCurrency(
                ticketCheckResult.value.winAmount
            )}</span>`
        )
        .replace(/\n/g, "<br>");
});

const faceAttributes = computed(() => {
    return ticketCheckResult.value.winAmount
        ? { type: DsSmileStaticFaceType.Stars, position: DsSmileStaticFacePosition.CenterDown }
        : { type: DsSmileStaticFaceType.Sad, position: DsSmileStaticFacePosition.RightDown };
});

onMounted(() => {
    if (ticketCheckResult.value.resultsTexts.tooltipText) {
        const popperElement = document.querySelector<HTMLDivElement>(
            ".pp-ticket-check-results-summary__tooltip .popper"
        );
        if (popperElement) {
            popperElement.style.display = "block";
        }
    }
});

// Analytics
const getBannerPosition = (idx: number) => {
    if (idx === 0) {
        return "top";
    }
    if (idx === ticketCheckResult.value.banners.length - 1) {
        return "bottom";
    }
    return "middle";
};
ticketCheckResult.value.banners.forEach((banner, idx) => {
    const promo: AnalyticsPromo = {
        id: banner.codeName,
        name: banner.codeName,
        creative: StringUtils.sanitizeString(banner.headline),
        position: getBannerPosition(idx),
    };

    ProductPageAnalytics.pushTicketCheckBannerImpress(promo);
});

const onBannerClick = (banner: DsPromoBannerNewDto, idx: number) => {
    const promo = {
        id: banner.codeName,
        name: banner.codeName,
        creative: StringUtils.sanitizeString(banner.headline),
        position: getBannerPosition(idx),
    };

    ProductPageAnalytics.pushTicketCheckBannerClick(promo);
};

const onButtonClick = (buttonText: string) => {
    ProductPageAnalytics.pushTicketCheckButtonClick(buttonText);
};
</script>
